import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Globe, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

import { AnalyticsCards, KiosksGrid, JobsTable } from '@/components/admin';
import AdminSEODashboard from '@/components/admin/AdminSEODashboard';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'seo'
  const [kiosks, setKiosks] = useState([]);
  const [printJobs, setPrintJobs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddKiosk, setShowAddKiosk] = useState(false);
  const [selectedKiosk, setSelectedKiosk] = useState(null);
  
  const [newKiosk, setNewKiosk] = useState({
    name: '',
    location: '',
    printer_ip: ''
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [kiosksRes, jobsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/kiosks`),
        axios.get(`${API}/print-jobs`),
        axios.get(`${API}/analytics/revenue?days=7`)
      ]);
      
      setKiosks(kiosksRes.data);
      setPrintJobs(jobsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAddKiosk = async () => {
    if (!newKiosk.name || !newKiosk.location || !newKiosk.printer_ip) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(`${API}/kiosks`, newKiosk);
      toast.success('Kiosk added successfully');
      setShowAddKiosk(false);
      setNewKiosk({ name: '', location: '', printer_ip: '' });
      fetchData();
    } catch (error) {
      console.error('Error adding kiosk:', error);
      toast.error('Failed to add kiosk');
    }
  };

  const handleCopyApiKey = (apiKey) => {
    navigator.clipboard.writeText(apiKey);
    toast.success('API key copied to clipboard');
  };

  const handleRegenerateApiKey = async (kioskId) => {
    if (!confirm('Are you sure? The current API key will stop working immediately.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/kiosks/${kioskId}/regenerate-key`);
      toast.success('API key regenerated. Update the print agent configuration.');
      fetchData();
    } catch (error) {
      console.error('Error regenerating key:', error);
      toast.error('Failed to regenerate API key');
    }
  };

  const handleViewDetails = (kiosk) => {
    setSelectedKiosk(kiosk);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-secondary flex flex-col justify-between">
      <div>
        {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="/inchpaper-printezy.png" 
                alt="Inchpaper" 
                className="h-10 sm:h-12 w-auto object-contain"
                onError={(e) => { e.target.src = '/inchpaper-logo.png'; }}
              />
              <div>
                <h1 className="font-heading text-3xl font-bold text-muted-foreground" data-testid="admin-title">
                  Admin Dashboard
                </h1>
                <p className="text-muted-foreground mt-1">Manage print kiosks, live jobs & platform SEO</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Tab Switcher */}
              <div className="bg-slate-100 p-1 rounded-xl flex items-center gap-1 border border-slate-200">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
                    activeTab === 'overview' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  <span>Kiosk Overview</span>
                </button>

                <button
                  onClick={() => setActiveTab('seo')}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${
                    activeTab === 'seo' ? 'bg-[#7d0909] text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span>SEO Command Center</span>
                </button>
              </div>

              <Dialog open={showAddKiosk} onOpenChange={setShowAddKiosk}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-kiosk-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Kiosk
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Kiosk</DialogTitle>
                    <DialogDescription>
                      Register a new print kiosk to your network
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Kiosk Name</Label>
                      <Input
                        placeholder="e.g., Store Front Kiosk"
                        value={newKiosk.name}
                        onChange={(e) => setNewKiosk({ ...newKiosk, name: e.target.value })}
                        data-testid="kiosk-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Location</Label>
                      <Input
                        placeholder="e.g., Main Street, Building A"
                        value={newKiosk.location}
                        onChange={(e) => setNewKiosk({ ...newKiosk, location: e.target.value })}
                        data-testid="kiosk-location-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Printer IP Address</Label>
                      <Input
                        placeholder="e.g., 192.168.1.100"
                        value={newKiosk.printer_ip}
                        onChange={(e) => setNewKiosk({ ...newKiosk, printer_ip: e.target.value })}
                        data-testid="printer-ip-input"
                      />
                    </div>
                    <Button className="w-full" onClick={handleAddKiosk} data-testid="save-kiosk-btn">
                      Add Kiosk
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        {activeTab === 'overview' ? (
          <>
            <AnalyticsCards analytics={analytics} kiosks={kiosks} />
            <KiosksGrid 
              kiosks={kiosks}
              onViewDetails={handleViewDetails}
              onCopyKey={handleCopyApiKey}
              onRegenerateKey={handleRegenerateApiKey}
            />
            <JobsTable printJobs={printJobs} />
          </>
        ) : (
          <AdminSEODashboard />
        )}
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
